// 画面ごとのチャンクの読み込みに失敗したときに、ページを再読み込みする。
//
// デプロイすると、ハッシュ付きの古いチャンクは Netlify から消える。デプロイ前に開いた
// タブで、まだ開いていない画面に移ると、古いチャンクを取りに行って失敗し、何も
// しなければ画面が真っ白になる。再読み込みすれば新しい index.html とチャンクを読める。
//
// Vite は動的 import に失敗すると window に vite:preloadError を出す。preventDefault
// すればエラーは投げられない。直前にも再読み込みしていた場合は、チャンク以外の原因で
// 失敗し続けているとみなし、再読み込みを繰り返さずにエラーをそのまま投げさせる。
// 投げたエラーは Router の Error Boundary が受け、本文の領域にエラー表示を出す。
//
// 直前に再読み込みしたかは sessionStorage に記録して判断する。読み書きできないときは
// 繰り返しを止められないので、再読み込みせずにエラーを投げさせる。

export const RELOADED_AT_KEY = 'geolonia:chunk-error-reloaded-at';

// この時間内に再び失敗したら、再読み込みしない
const RELOAD_INTERVAL_MS = 10_000;

type Options = {
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  now: () => number;
};

// window.sessionStorage は、Cookie をブロックしているブラウザでは参照しただけで例外になる。
// 起動時に参照するとアプリ全体の初期化が止まるので、読み書きするときに初めて参照する。
// そこで投げた例外は createChunkErrorHandler の try-catch で受ける。
export const lazySessionStorage = (): Options['storage'] => ({
  getItem: (key) => window.sessionStorage.getItem(key),
  setItem: (key, value) => window.sessionStorage.setItem(key, value),
});

const createChunkErrorHandler = ({ storage, reload, now }: Options) =>
  (event: Pick<Event, 'preventDefault'>): void => {
    let reloadedAt: number | null;
    try {
      const value = storage.getItem(RELOADED_AT_KEY);
      reloadedAt = value === null ? null : Number(value);
    } catch {
      return;
    }

    // 端末の時刻が戻って記録が未来になったときは、経過時間が負になる。それで再読み込みを
    // 止め続けないよう、0 以上のときだけ直前の再読み込みとみなす
    const elapsed = reloadedAt === null ? null : now() - reloadedAt;
    if (elapsed !== null && elapsed >= 0 && elapsed < RELOAD_INTERVAL_MS) {
      return;
    }

    try {
      storage.setItem(RELOADED_AT_KEY, String(now()));
    } catch {
      return;
    }
    event.preventDefault();
    reload();
  };

export default createChunkErrorHandler;
